// Reine Logik rund um die Bilder eines Termins - ohne React, ohne Firebase.
//
// Was tatsaechlich hochlaedt, steht in src/components/shared/EventEditor.js;
// hier steht nur, was aus einem Termin-Dokument anzeigbar ist. Bewusst ohne
// Firebase-Import, damit es aus Jest direkt testbar bleibt (Muster:
// imageCrop.js, miniBingoUpload.js).

import { isSafeHttpUrl } from './links';
import { EVENT_PHOTOS_MAX, EVENT_PHOTO_CAPTION_MAX } from './constants';

/**
 * Das Titelbild eines Termins - oder null.
 *
 * Die Pruefung mit isSafeHttpUrl ist kein Selbstzweck: firestore.rules prueft
 * bei imageUrl nur Typ und Laenge, weil ein Regex auf Storage-Adressen den
 * Emulator und kuenftige Buckets mit aussperren wuerde. Ein "javascript:"-Wert
 * landete sonst im src des <img> - dieselbe Ueberlegung wie bei event.link in
 * EventCard.js.
 */
export function coverOf(event) {
  const url = event?.imageUrl;
  return isSafeHttpUrl(url) ? url.trim() : null;
}

/**
 * Die weiteren Bilder eines Termins als sauberes Array.
 *
 * Bestandstermine haben das Feld gar nicht, und ein Eintrag ohne brauchbare
 * Adresse waere ein kaputtes Bild in der Galerie - beides faellt hier heraus,
 * statt an jeder Anzeigestelle erneut geprueft zu werden.
 */
export function photosOf(event) {
  const list = Array.isArray(event?.photos) ? event.photos : [];
  return list
    .filter((photo) => isSafeHttpUrl(photo?.url))
    .map((photo) => ({
      url: photo.url.trim(),
      path: typeof photo.path === 'string' ? photo.path : '',
      caption: typeof photo.caption === 'string' ? photo.caption.trim() : '',
    }));
}

/**
 * Neue Bilder anhaengen, ohne die Obergrenze zu reissen.
 *
 * Gibt zusaetzlich `skipped` zurueck: Waehlt jemand zwoelf Dateien aus,
 * obwohl nur noch drei Plaetze frei sind, muss die Maske das sagen koennen -
 * neun Bilder stillschweigend zu verschlucken waere der schlechtere Weg.
 */
export function addPhotos(list, incoming, max = EVENT_PHOTOS_MAX) {
  const current = Array.isArray(list) ? list : [];
  const additions = Array.isArray(incoming) ? incoming : [];
  const free = Math.max(0, max - current.length);

  return {
    photos: [...current, ...additions.slice(0, free)],
    skipped: Math.max(0, additions.length - free),
  };
}

/** Ein Bild aus der Liste nehmen. */
export function removePhotoAt(list, index) {
  const current = Array.isArray(list) ? list : [];
  if (index < 0 || index >= current.length) return current;
  return current.filter((_, i) => i !== index);
}

/** Bildunterschrift eines einzelnen Bildes aendern. */
export function setPhotoCaption(list, index, caption) {
  const current = Array.isArray(list) ? list : [];
  if (index < 0 || index >= current.length) return current;
  return current.map((photo, i) =>
    i === index ? { ...photo, caption: (caption ?? '').slice(0, EVENT_PHOTO_CAPTION_MAX) } : photo
  );
}

/**
 * Alternativtext eines Terminfotos.
 *
 * Die Bildunterschrift ist der bessere Text, wenn es eine gibt - genau wie in
 * der Galerie, wo die Unterschrift ausdruecklich auch als Alternativtext
 * dient. Ohne sie bleibt die Zaehlung: "Foto 2 von Sommer-Ausfahrt" sagt einem
 * Screenreader mehr als ein leeres alt.
 */
export function photoAlt(event, photo, index) {
  if (photo?.caption) return photo.caption;
  const title = event?.title ? `: ${event.title}` : '';
  return `Foto ${index + 1}${title}`;
}

/**
 * Alle Storage-Pfade eines Termins - Titelbild und Galerie.
 *
 * Gegenstueck zur Loeschkaskade im Backend
 * (functions/handlers/eventHandlers.js): Was hier steht, muss beim Loeschen
 * eines Termins aus dem Bucket verschwinden.
 */
export function storagePathsOf(event) {
  return [event?.imagePath, ...(Array.isArray(event?.photos) ? event.photos : []).map((p) => p?.path)]
    .filter((path) => typeof path === 'string' && path.length > 0);
}
